<template>
  <div
    class="opinion-card"
    :class="{ selected: selected }"
    @click="$emit('select')"
  >
    <!-- 头部行 -->

    <div class="card-row">
      <span class="num-badge">{{ badgeNum }}</span>
      <span class="card-title">{{ opinion.title }}</span>
      <span class="risk-tag" :class="'risk-' + opinion.riskLevel">{{
        opinion.riskLabel
      }}</span>
    </div>

    <!-- meta 行 -->
    <div class="meta-row">
      <span v-for="m in opinion.meta" :key="m" class="meta-tag">{{ m }}</span>
      <span
        v-if="!readonly"
        class="meta-tag"
        :class="{ 'tag-applied': opinion.applied }"
        >{{ opinion.applied ? "已应用" : "未应用" }}</span
      >
    </div>

    <!-- 详情区：仅选中时展开（手风琴）；顶部 border 即分割线 -->
    <div v-if="selected" class="card-detail">
      <!-- 风险概述 -->
      <div class="field-label">风险概述</div>
      <div class="field-desc">{{ opinion.riskDesc }}</div>

      <!-- 修改方案 -->
      <div class="field-label field-label-mt">修改方案</div>
      <div class="field-desc">{{ opinion.suggestion }}</div>

      <!-- 修订版 / 清洁版 切换（无可应用修订内容时整体隐藏） -->
      <div
        v-if="!readonly && hasRevision"
        class="version-row"
        :class="{ locked: applied }"
        @click.stop
      >
        <div
          class="version-item"
          :class="{ active: version === 'revised' }"
          @click="!applied && (version = 'revised')"
        >
          修订版
        </div>
        <div
          class="version-item"
          :class="{ active: version === 'clean' }"
          @click="!applied && (version = 'clean')"
        >
          清洁版
        </div>
      </div>

      <!-- 修改内容：修订版 / 清洁版 切换（无可应用修订内容时整体隐藏） -->
      <div
        v-if="!readonly && hasRevision"
        class="modify-box"
        v-html="modifyContent"
      ></div>

      <!-- 操作按钮 -->
      <div v-if="!readonly" class="action-row" @click.stop>
        <button class="btn-outline" @click="$emit('locate', opinion)">
          定位原文
        </button>
        <button
          v-if="!applied"
          class="btn-primary-sm"
          :disabled="!hasRevision"
          @click="$emit('apply', { opinion, version, content: applyContent })"
        >
          应用修订
        </button>
        <button v-else class="btn-cancel-sm" @click="$emit('cancel', opinion)">
          取消修订
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  opinion: { type: Object, required: true },
  // 展示序号：父组件按当前 tab 重新编号（从 1 开始）。不传则回退到 opinion.num
  displayNum: { type: [Number, String], default: null },
  selected: { type: Boolean, default: false },
  // 是否已应用修订（驱动按钮文案 + 版本 tab 锁定）
  applied: { type: Boolean, default: false },
  // 只读模式（PDF 合同）：隐藏版本切换/修改内容/操作按钮/已应用标签
  readonly: { type: Boolean, default: false },
});

defineEmits(["locate", "apply", "cancel", "select"]);

// badge 显示的序号：优先 displayNum（per-tab 重新编号），否则回退 opinion.num
const badgeNum = computed(() =>
  props.displayNum != null ? props.displayNum : props.opinion.num,
);

const version = ref("revised");

// 是否有可应用的修订内容：修订版、清洁版均为空时视为没有，
// 此时隐藏版本切换/修改内容区域，并禁用“应用修订”按钮
const hasRevision = computed(
  () =>
    Boolean(props.opinion.revisedVersion) ||
    Boolean(props.opinion.cleanVersion),
);

// 修订版 → opinion.revisedVersion（后端 markedVersion）；
// 清洁版 → opinion.cleanVersion（后端 cleanVersion）
const modifyContent = computed(() =>
  version.value === "clean"
    ? props.opinion.cleanVersion || ""
    : props.opinion.revisedVersion || "",
);

// 应用修订统一写入清洁版（cleanVersion 纯文本，无颜色标记）；
// 修订版 tab 仅作展示对照，不再作为写入内容
const applyContent = computed(() => props.opinion.cleanVersion || "");
</script>

<style lang="less" scoped>
.opinion-card {
  border: 1px solid #e4eaf4;
  border-radius: 16px;
  margin: 12px 12px 0 12px;
  padding: 14px 16px 16px 16px;
  box-shadow: 0 4px 14px rgba(31, 57, 105, 0.04);
  background: white;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover {
    border-color: #b0c4e8;
    box-shadow: 0 4px 14px rgba(31, 57, 105, 0.08);
  }

  &.selected {
    border-color: #8fb7ff;
    box-shadow: 0 0 0 2px #eaf2ff;
  }
}

/* 详情区：仅选中时展开；顶部分割线颜色与卡片边框一致 */
.card-detail {
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid #e4eaf4;
}

.card-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.num-badge {
  background: #eaf2ff;
  border-radius: 8px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #1768ff;
  font-weight: 600;
  flex-shrink: 0;
}
.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2f4a;
}
.risk-tag {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  margin-left: auto;
  white-space: nowrap; // 风险等级文案不换行
  flex-shrink: 0; // flex 布局下不被挤压变形
  &.risk-high {
    background: #fff1f1;
    color: #ef4444;
  }
  &.risk-medium {
    background: #fff5e8;
    color: #b66f11;
  }
  &.risk-low {
    background: #f4edff;
    color: #6d28d9;
  }
}

.meta-row {
  display: flex;
  gap: 6px;
  margin: 6px 0 8px 34px;
}
.meta-tag {
  background: #f4f6fa;
  color: #718096;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  &.tag-applied {
    background: #eaf8f2;
    color: #168760;
  }
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: #1f2f4a;
  position: relative;
  padding-left: 8px;
  &::after {
    content: "";
    width: 3px;
    height: 13px;
    border-radius: 2px;
    background: #1768ff;
    position: absolute;
    left: 0px;
  }
  &.field-label-mt {
    margin-top: 8px;
  }
}
.field-desc {
  font-size: 13px;
  color: #4c5c73;
  line-height: 1.6;
  margin-top: 4px;
}

.version-row {
  background: #f2f5fa;
  border-radius: 8px;
  padding: 4px 6px;
  display: flex;
  gap: 8px;
  margin: 10px 0 6px;
  text-align: center;
  &.locked {
    opacity: 0.6;
  }
}
.version-item {
  flex: 1;
  padding: 4px 20px;
  color: #718096;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s;
  &.active {
    background: white;
    box-shadow: 0 2px 6px rgba(31, 57, 105, 0.07);
    color: #1768ff;
    font-weight: 600;
  }
  .locked & {
    cursor: not-allowed;
  }
}

.modify-box {
  background: #f7fbff;
  border: 1px solid #dce7fb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #34445f;
  line-height: 1.6;
  margin: 6px 0 10px;
  :deep(.hl-red) {
    color: #d83232;
    font-weight: 600;
    text-decoration: underline;
  }
}

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  justify-content: space-between;
}
.btn-outline {
  border: 1px solid #dce5f1;
  border-radius: 8px;
  padding: 4px 16px;
  height: 34px;
  font-size: 13px;
  color: #41536d;
  background: white;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #1768ff;
    color: #1768ff;
  }
}
.btn-primary-sm {
  background: #1768ff;
  border: 1px solid #1768ff;
  border-radius: 8px;
  padding: 4px 16px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: #0d5de8;
  }
  &:disabled {
    background: #a9c6f2;
    border-color: #a9c6f2;
    cursor: not-allowed;
  }
}
.btn-cancel-sm {
  background: white;
  border: 1px solid #d83232;
  border-radius: 8px;
  padding: 4px 16px;
  color: #d83232;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #fff1f1;
  }
}
</style>
